import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import useDebouncedValue from '../hooks/useDebouncedValue';
import {
  fetchActivePartById,
  fetchActiveServiceById,
  searchCatalogRefs
} from '../utils/catalogSearch';

const NEW_PART = '__new_part__';

function optionLabel(opt) {
  if (!opt) return '';
  if (opt.rowKind === 'new_part') return 'New Part…';
  if (opt.rowKind === 'part') {
    const p = opt.data;
    const num = p.part_number != null ? String(p.part_number).trim() : '';
    const desc = String(p.part_description ?? p.description ?? '').trim();
    if (!num && !desc) return '';
    return desc ? `${num || '—'} — ${desc}` : num || '';
  }
  const s = opt.data;
  const code = effectiveServiceCode(s) || '';
  const desc = String(s.service_description ?? s.description ?? '').trim();
  if (!code && !desc) return '';
  return desc ? `${code || '—'} — ${desc}` : code || '';
}

function buildRowOptions(parts, services) {
  const o = [{ rowKind: 'new_part', key: NEW_PART }];
  for (const p of parts) o.push({ rowKind: 'part', key: `p:${p.id}`, data: p });
  for (const s of services) o.push({ rowKind: 'service', key: `s:${s.id}`, data: s });
  return o;
}

function mergePinnedRowOptions(options, pinned) {
  if (!pinned) return options;
  if (options.some((o) => o.key === pinned.key)) return options;
  const newRow = options.find((o) => o.rowKind === 'new_part');
  const rest = options.filter((o) => o.rowKind !== 'new_part');
  return newRow ? [newRow, pinned, ...rest] : [pinned, ...rest];
}

async function resolvePinnedRowOption(editedItem) {
  if (!editedItem) return null;
  const it = editedItem.item_type || 'part';
  const sid = editedItem.service_id;
  const pid = editedItem.part_id;
  if (it === 'service' && sid != null && String(sid).trim() !== '') {
    const service = await fetchActiveServiceById(sid);
    return service ? { rowKind: 'service', key: `s:${service.id}`, data: service } : null;
  }
  if (pid != null && String(pid).trim() !== '') {
    const part = await fetchActivePartById(pid);
    return part ? { rowKind: 'part', key: `p:${part.id}`, data: part } : null;
  }
  return null;
}

/**
 * Part/service picker for order "materials" section.
 * @param {(raw: string, record?: { kind: 'part'|'service', data: object }|null) => void} onCommit
 */
export default function OrderLineMaterialsRefAutocomplete({
  editedItem,
  onCommit,
  size = 'small',
  disabled = false,
  label = 'Part',
  textFieldSx,
  noMargin = false
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([{ rowKind: 'new_part', key: NEW_PART }]);
  const [loading, setLoading] = useState(false);
  const [pinnedOption, setPinnedOption] = useState(null);
  const debouncedInput = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pinned = await resolvePinnedRowOption(editedItem);
      if (!cancelled) setPinnedOption(pinned);
    })();
    return () => {
      cancelled = true;
    };
  }, [editedItem?.item_type, editedItem?.part_id, editedItem?.service_id]);

  useEffect(() => {
    let cancelled = false;
    const q = debouncedInput.trim();
    if (!q) {
      const base = [{ rowKind: 'new_part', key: NEW_PART }];
      setOptions(pinnedOption ? mergePinnedRowOptions(base, pinnedOption) : base);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const { parts, services } = await searchCatalogRefs(q);
        if (cancelled) return;
        const built = buildRowOptions(parts, services);
        setOptions(mergePinnedRowOptions(built, pinnedOption));
      } catch (err) {
        console.error('Catalog search failed:', err);
        if (!cancelled) {
          const base = [{ rowKind: 'new_part', key: NEW_PART }];
          setOptions(pinnedOption ? mergePinnedRowOptions(base, pinnedOption) : base);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedInput, pinnedOption]);

  const value = useMemo(() => {
    if (!editedItem) return null;
    const it = editedItem.item_type || 'part';
    const sid = editedItem.service_id;
    const pid = editedItem.part_id;
    if (it === 'service' && sid != null && String(sid).trim() !== '') {
      return options.find((x) => x.rowKind === 'service' && String(x.data.id) === String(sid)) || pinnedOption;
    }
    if (pid != null && String(pid).trim() !== '') {
      return options.find((x) => x.rowKind === 'part' && String(x.data.id) === String(pid)) || pinnedOption;
    }
    return null;
  }, [editedItem, options, pinnedOption]);

  return (
    <Autocomplete
      size={size}
      disabled={disabled}
      options={options}
      value={value}
      loading={loading}
      filterOptions={(opts) => opts}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === 'reset') return;
        setInputValue(newInputValue);
      }}
      onChange={(_e, v) => {
        if (!v) {
          onCommit('', null);
          return;
        }
        if (v.rowKind === 'new_part') {
          onCommit(NEW_PART, null);
          return;
        }
        setPinnedOption(v);
        if (v.rowKind === 'part') onCommit(String(v.data.id), { kind: 'part', data: v.data });
        else onCommit(String(v.data.id), { kind: 'service', data: v.data });
      }}
      getOptionLabel={(opt) => optionLabel(opt)}
      isOptionEqualToValue={(a, b) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.key === b.key;
      }}
      noOptionsText={
        loading ? 'Searching…' : inputValue.trim() ? 'No parts or services found' : 'Type to search'
      }
      renderOption={(props, opt) => (
        <li {...props} key={opt.key}>
          {optionLabel(opt)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          // Table cells already have a column header; floating labels bleed into the row above.
          label={noMargin ? undefined : label}
          placeholder="Search parts or services…"
          InputLabelProps={noMargin ? undefined : { ...params.InputLabelProps, shrink: true }}
          inputProps={{
            ...params.inputProps,
            'aria-label': label || 'Part / service'
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={textFieldSx}
          margin={noMargin ? 'none' : 'dense'}
        />
      )}
      ListboxProps={{ style: { maxHeight: 320 } }}
    />
  );
}
